import { ExceptionEvent, ExceptionEventState } from "@fairfleet/geotab";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CommentSection from "../CommentSection";
import AccidentDataTable from "./AccidentDataTable";
import confirmAccident from "./confirmAccident";
import GForcesDetailChart from "./GForcesDetailChart";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { useUpdateExceptionEvent } from "@/hooks/exceptionEvent/useUpdateExceptionEvent";
import { useAddFeedback } from "@/hooks/feedback/addFeedback";
import { useFeedback } from "@/hooks/feedback/useFeedback";
import { useLogRecords } from "@/hooks/logRecord/useLogRecords";
import { useRulesByName } from "@/hooks/rules/useRulesByName";
import { useHistoricalStatusData } from "@/hooks/statusData/useHistoricalStatusData";
import isDemo from "@/utilities/isDemo";

export default function AccidentDetail({ event }: { event: ExceptionEvent }) {
  const { data: adRuleNames = [] } = useRulesByName(["[AD]%"]);
  const { geotabClient, me } = useGeotabClient();

  const { activeFrom, activeTo } = event;
  const deviceId = event.device?.id;
  const fromDate = new Date((activeFrom as Date)?.getTime?.() ?? Date.now() - 10000);
  const toDate = new Date((activeTo as Date)?.getTime?.() ?? Date.now());

  const { data: statusData = {} } = useHistoricalStatusData(
    ["DiagnosticAccelerationForwardBrakingId", "DiagnosticAccelerationSideToSideId"],
    [deviceId],
    fromDate,
    toDate,
  );
  const rId = event?.rule?.id;
  const forwardBreakingGForces = useMemo(
    () => statusData["DiagnosticAccelerationForwardBrakingId"]?.[deviceId] ?? [],
    [deviceId, statusData],
  );
  const sideTosideGForces = useMemo(
    () => statusData["DiagnosticAccelerationSideToSideId"]?.[deviceId] ?? [],
    [deviceId, statusData],
  );

  const { data: logRecords = [] } = useLogRecords(deviceId, fromDate, toDate);
  const rule = adRuleNames?.[0]?.find(d => d.id === rId);
  const [open, setOpen] = useState(false);

  const { data: feedbacks = [] } = useFeedback(event.id);
  const myFeedback = feedbacks?.find(d => d.author.id === me?.id);
  const updateExceptionEvent = useUpdateExceptionEvent();
  const addFeedback = useAddFeedback(event.device?.id, event.id);

  const verifyAccident = () => {
    if (!confirm("Are you sure you want to verify this accident?")) return;
    if (!event || !event.diagnostic || !event.rule || !me || !event.device) return;
    confirmAccident(
      geotabClient,
      event.diagnostic?.id,
      event.rule?.id,
      me,
      event.device,
      event.id,
      true,
    );
    addFeedback.mutate(true);
  };

  const dismissAccident = () => {
    if (!confirm("Are you sure you want to dismiss this accident?")) return;
    if (!event || !event.diagnostic || !event.rule || !me || !event.device) return;
    confirmAccident(
      geotabClient,
      event.diagnostic?.id,
      event.rule?.id,
      me,
      event.device,
      event.id,
      false,
    );
    updateExceptionEvent.mutate({
      entity: {
        ...event,
        state: "ExceptionEventStateDismissedId" as unknown as ExceptionEventState,
      },
      geotabClient,
    });
    addFeedback.mutate(false);
  };

  const { t } = useTranslation();

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
        <Typography variant="h6">{rule?.name?.replace("[AD] -", "")?.trim() ?? ""}</Typography>
        {(event.state as unknown as string) === "ExceptionEventStateDismissedId" ? (
          <Tooltip title="Dismissed">
            <CancelIcon color="error" />
          </Tooltip>
        ) : (
          <Tooltip title="Valid">
            <CheckCircleIcon color="success" />
          </Tooltip>
        )}
        <QueryStatsIcon color="primary" onClick={() => setOpen(true)} sx={{ ml: 1 }} />
      </Box>
      <AccidentDataTable event={event} />
      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        {myFeedback ? (
          <Alert
            icon={<CheckCircleIcon fontSize="inherit" />}
            severity="success"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {myFeedback.feedbackStatus === "Valid"
                ? t("faultOverview.accidentVerificationSuccess")
                : t("faultOverview.accidentDismissSuccess")}
            </Typography>
          </Alert>
        ) : (
          <>
            <Button variant="contained" onClick={verifyAccident} disabled={isDemo}>
              {t("faultOverview.verify")}
            </Button>
            <Button onClick={dismissAccident} disabled={isDemo}>{"Verwerfen"}</Button>
          </>
        )}
      </Box>
      <CommentSection event={event} />
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
          }}
        >
          <Box sx={{ width: "100%", height: "80vh" }}>
            <GForcesDetailChart
              sideTosideGForces={sideTosideGForces}
              forwardBreakingGForces={forwardBreakingGForces}
              logRecords={logRecords}
            />
          </Box>
          <Button
            variant="outlined"
            sx={{ mt: 2, display: "block", mx: "auto" }}
            onClick={() => setOpen(false)}
          >
            {t("faultOverview.close")}
          </Button>
        </Box>
      </Modal>
    </>
  );
}
